import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Divider, IconButton, Link, Modal, Paper, Stack, Tooltip, Typography } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { MemberLifecycle, MemberLifecycleProgressItem } from '../../../Models/MemberLifecycle/MemberLifecycle.model';
import { formatUtcDate, formatUtcDateTime } from '../../../utils/timeFormat';
import Authorization from '../../../utils/Authorization';
import { RootState } from '../../../reducers';
import { useAppDispatch } from '../../../store/store';
import { recalculateMemberLifecycle } from '../../../store/memberLifecycle.slice';

// Mock data for lifecycle stages and progress
export const lifecycleStages = [
    {
        stageKey: 'referral',
        label: 'Referral',
        color: '#C9363A',
        lightColor: '#D65A56',
        substages: [
            { substageKey: 'referral-list', label: 'Referral List' },
            { substageKey: 'confirm-servicable', label: 'Confirm Servicable' },
        ],
        summary: 'Member has entered the CHP referral pathway and is being reviewed for initial fit and serviceability.',
        reasons: ['Confirm the member is appropriate for CHP outreach', 'Identify early barriers before onboarding begins', 'Create a clean handoff for the onboarding team'],
    },
    {
        stageKey: 'onboarding',
        label: 'Onboarding',
        color: '#744385',
        lightColor: '#8D6399',
        substages: [
            { substageKey: 'research', label: 'Research' },
            { substageKey: 'contact', label: 'Contact' },
            { substageKey: 'onboard', label: 'Onboard' },
        ],
        summary: 'Team gathers background information, contacts the member, and prepares the first meaningful engagement.',
        reasons: ['Build enough context to make outreach personal', 'Reduce repeated questions for the member', 'Document why the member is ready to move into relationship building'],
    },
    {
        stageKey: 'relationship-building',
        label: 'Relationship Building',
        color: '#F1B83C',
        lightColor: '#F4C85D',
        substages: [
            { substageKey: 'schedule', label: 'Schedule' },
            { substageKey: 'first-appointment', label: 'First Appointment' },
            { substageKey: 'care-planning', label: 'Care Planning' },
            { substageKey: 'identify-and-meet-member-needs', label: 'Identify and Meet Member Needs' },
        ],
        summary: 'CHP is establishing trust, completing early visits, and turning assessment findings into a member-centered care plan.',
        reasons: ['Make the first care plan actionable', 'Connect member goals to services and team follow-up', 'Show why the member is progressing toward active engagement'],
    },
    {
        stageKey: 'engage',
        label: 'Engage',
        color: '#2C9CAD',
        lightColor: '#47AEB8',
        substages: [
            { substageKey: 'ongoing-care', label: 'Ongoing Care' },
            { substageKey: 'transitional-care', label: 'Transitional Care' },
        ],
        summary: 'Member is actively engaged with CHP through ongoing care coordination and transition support.',
        reasons: ['Maintain momentum after care planning', 'Prevent gaps after ED, inpatient, or specialist transitions', 'Track whether interventions are improving member stability'],
    },
    {
        stageKey: 'change-coordination',
        label: 'Change Coordination',
        color: '#42A982',
        lightColor: '#61B694',
        substages: [{ substageKey: 'stabilize', label: 'Stabilize' }],
        summary: 'Care team is coordinating change activities to stabilize the member and reduce avoidable escalation.',
        reasons: ['Clarify what needs to change for the member to stabilize', 'Keep partners aligned around the same plan', 'Document evidence that risks are improving or still active'],
    },
    {
        stageKey: 'support',
        label: 'Support',
        color: '#3F73B7',
        lightColor: '#4F7FC0',
        substages: [{ substageKey: 'independence-and-habit-forming', label: 'Independence and Habit Forming' }],
        summary: 'Member is building independence and durable habits with CHP support stepping down over time.',
        reasons: ['Show readiness for less intensive support', 'Reinforce habits that keep the member stable', 'Prepare the member and care team for graduation criteria'],
    },
    {
        stageKey: 'graduation',
        label: 'Graduation',
        color: '#D98DB3',
        lightColor: '#E2A1C2',
        substages: [
            { substageKey: 'identify-readiness', label: 'Identify Readiness' },
            { substageKey: 'graduation-appointment', label: 'Graduation Appointment' },
        ],
        summary: 'Member is preparing to graduate after meeting stability, engagement, and self-management milestones.',
        reasons: ['Confirm the member has a sustainable plan after CHP', 'Celebrate progress and close the episode intentionally', 'Document why graduation is clinically and operationally appropriate'],
    },
];

export const splitJourneySummaryParagraphs = (summary?: string | null) => {
    const trimmed = summary?.trim();
    if (!trimmed) {
        return [];
    }

    return trimmed
        .split(/\n\s*\n/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);
};

export const lifecycleSubstageFallbacks: Record<string, { documentation: string[]; notCompleteReasons: string[] }> = {
    'referral:referral-list': {
        documentation: ['Referral date on member profile', 'Outbound call log outreach activity', 'Staff referral or document engagement on chart'],
        notCompleteReasons: ['Referral date is not recorded', 'No outbound call log, staff referral, or document engagement intake activity on file'],
    },
    'referral:confirm-servicable': {
        documentation: ['Member AHCCCS eligibility and contracting health plan', 'Care team assignment', 'Additional CHP criteria assessment completed'],
        notCompleteReasons: [
            'Member is marked deceased or not age-eligible for CHP',
            'No care team assignment on member or roster',
            'AHCCCS eligibility is not active and no AHCCCS member ID on file',
        ],
    },
    'onboarding:research': {
        documentation: ['Member demographics (name, DOB, phone, city)', "Onboarder's note activity", 'Completed onboarding appointment (onboarding visit, health risk assessment, or FUHFUM)'],
        notCompleteReasons: ['Member demographics are not complete (name, DOB, phone, and city required)', "No onboarder's note on file"],
    },
    'onboarding:contact': {
        documentation: ['Outbound call log contact attempts', "Onboarder's note contact activity", 'Onboarding appointment scheduled or completed (onboarding visit, health risk assessment, or FUHFUM)'],
        notCompleteReasons: ["No outbound call log contact attempts or onboarder's note logged", 'No onboarding appointment scheduled or completed (onboarding visit, health risk assessment, or FUHFUM)'],
    },
    'onboarding:onboard': {
        documentation: [
            'Completed onboarding appointment (onboarding visit, health risk assessment, or FUHFUM)',
            'Date of onboard on member profile',
            'Member assessment (consent and initial assessments)',
            'Follow-up appointment after onboard',
        ],
        notCompleteReasons: [
            'No completed onboarding appointment (onboarding visit, health risk assessment, or FUHFUM)',
            'Date of onboard is not recorded',
            'Consent member assessment is not completed',
            'No completed member assessment beyond consent',
            'No follow-up appointment scheduled after onboarding',
        ],
    },
    'relationship-building:schedule': {
        documentation: ['Recurring appointment series on chart', 'Earliest recurrence start date'],
        notCompleteReasons: ['No recurring appointment with recurrence string on file'],
    },
    'relationship-building:first-appointment': {
        documentation: ['Documented visit after date of onboard'],
        notCompleteReasons: ['No documented visit after onboarding'],
    },
    'relationship-building:care-planning': {
        documentation: ['Integrated care plan with active or achieved care goals', 'Care goal and barrier counts'],
        notCompleteReasons: ['No integrated care plan with in-progress or achieved care goal on file'],
    },
    'relationship-building:identify-and-meet-member-needs': {
        documentation: [
            'SDOH assessment on file',
            'Member need services recorded or fulfilled',
            'Completed request records',
            'PCP and specialist and pharmacy on member profile',
        ],
        notCompleteReasons: [
            'No completed SDOH assessment on file',
            'No member need services or completed request documented',
            'PCP is not documented on member profile',
            'Pharmacy is not documented on member profile',
        ],
    },
    'engage:ongoing-care': {
        documentation: [
            'Documented visits and completed appointment touchpoints (last 90 days)',
            'HEDIS measures on member profile',
            'Member last-visit fields (last CHP visit, etc.)',
        ],
        notCompleteReasons: [
            'Fewer than required documented visit or appointment touchpoints in the last 90 days',
            'No HEDIS or preventive-care signals on member profile or care plan',
        ],
    },
    'engage:transitional-care': {
        documentation: ['Transitional care episode and transition steps', 'In-transition flag on member profile', 'HIE ADT alert activity'],
        notCompleteReasons: ['No transitional care episode documented', 'No transition flags or HIE ADT alerts on file'],
    },
    'change-coordination:stabilize': {
        documentation: ['Achieved care goal count', 'HIE ADT alert 90-day trend', 'Eligibility recheck (AHCCCS)'],
        notCompleteReasons: ['No achieved care goal records or AHCCCS eligibility not active', 'ADT alert volume has not improved over the rolling window'],
    },
    'support:independence-and-habit-forming': {
        documentation: ['Appointment kept rate (last 90 days)', 'Achieved care goal count', 'Member survey readiness completion'],
        notCompleteReasons: [
            'Appointment kept rate below threshold',
            'Insufficient achieved care goal records',
            'No completed member survey readiness survey',
        ],
    },
    'graduation:identify-readiness': {
        documentation: ['Member status (preparing for graduation)', 'Tenure from date of onboard', 'Achieved care goal count', 'Member survey readiness'],
        notCompleteReasons: ['Member status is not preparing for graduation and tenure, goals, or survey criteria are not met'],
    },
    'graduation:graduation-appointment': {
        documentation: ['Completed graduation appointment', 'Member survey end-of-service completion', 'Tablet recovery status'],
        notCompleteReasons: ['No completed graduation appointment on file', 'Member survey end-of-service not completed'],
    },
};

export const getLifecycleSubstageFallback = (stageKey: string, substageKey: string) => {
    const key = `${stageKey}:${substageKey}`;
    return lifecycleSubstageFallbacks[key] ?? { documentation: [] as string[], notCompleteReasons: [] as string[] };
};

export const lifecycleSubstageChartTab: Record<string, string> = {
    'referral:referral-list': 'member-profile',
    'referral:confirm-servicable': 'member-profile',
    'onboarding:research': 'onboarding-engagement',
    'onboarding:contact': 'onboarding-engagement',
    'onboarding:onboard': 'assessment-dashboard',
    'relationship-building:schedule': 'scheduling',
    'relationship-building:first-appointment': 'scheduling',
    'relationship-building:care-planning': 'integrated-care-plan',
    'relationship-building:identify-and-meet-member-needs': 'member-requests',
    'engage:ongoing-care': 'scheduling',
    'engage:transitional-care': 'transitional-care',
    'change-coordination:stabilize': 'integrated-care-plan',
    'support:independence-and-habit-forming': 'scheduling',
    'graduation:identify-readiness': 'member-profile',
    'graduation:graduation-appointment': 'scheduling',
};

export type LifecycleStage = (typeof lifecycleStages)[number];

export type SelectedLifecycleItem = {
    stage: LifecycleStage;
    substageKey: string;
    substage: string;
    isCompleted: boolean;
    isCurrent: boolean;
    memberId?: string;
    completedDate?: string;
    summary?: string | null;
    documentation?: string[];
    reasons?: string[];
    notCompleteReasons?: string[];
};

const leftColumnWidth = 160;
const minimumChartWidth = leftColumnWidth + lifecycleStages.reduce((total, stage) => total + stage.substages.length, 0) * 120;
const defaultStageKey = 'referral';
const defaultSubstageKey = 'referral-list';

export const getLifecycleStage = (stageKey?: string | null) => lifecycleStages.find((stage) => stage.stageKey === stageKey) ?? lifecycleStages[0];

export const getLifecycleSubstage = (stageKey?: string | null, substageKey?: string | null) => {
    const stage = getLifecycleStage(stageKey);
    return stage.substages.find((substage) => substage.substageKey === substageKey) ?? stage.substages[0];
};

export const getStageOrdinal = (stageKey?: string | null, substageKey?: string | null) => {
    let ordinal = 0;
    for (const stage of lifecycleStages) {
        for (const substage of stage.substages) {
            if (stage.stageKey === stageKey && substage.substageKey === substageKey) {
                return ordinal;
            }
            ordinal += 1;
        }
    }
    return 0;
};

export const isProgressItemComplete = (progressItem?: MemberLifecycleProgressItem | null) =>
    Boolean(progressItem?.IsCompleted && progressItem?.CompletedDate);

export const getLastCompletedOrdinal = (progressItems?: MemberLifecycleProgressItem[] | null) => {
    let lastCompleted = -1;
    let ordinal = 0;

    for (const stage of lifecycleStages) {
        for (const substage of stage.substages) {
            const progressItem = progressItems?.find((item) => item.StageKey === stage.stageKey && item.SubstageKey === substage.substageKey);
            if (isProgressItemComplete(progressItem)) {
                lastCompleted = ordinal;
            }
            ordinal += 1;
        }
    }

    return lastCompleted;
};

export const getFillThroughOrdinal = (progressItems: MemberLifecycleProgressItem[] | null | undefined, currentOrdinal: number) => {
    const lastCompletedOrdinal = getLastCompletedOrdinal(progressItems);
    return lastCompletedOrdinal >= 0 ? lastCompletedOrdinal : currentOrdinal;
};

export const isSubstageCurrentProgress = (substageOrdinal: number, fillThroughOrdinal: number) => substageOrdinal === fillThroughOrdinal;

export const formatLifecycleCompletedDate = (completedDate?: string | null) => {
    if (!completedDate) {
        return undefined;
    }

    const formatted = formatUtcDate(completedDate);
    return formatted === '-' ? completedDate : formatted;
};

const getProgressItem = (memberLifecycle: MemberLifecycle | null | undefined, stageKey: string, substageKey: string) =>
    memberLifecycle?.ProgressItems?.find((item) => item.StageKey === stageKey && item.SubstageKey === substageKey);

export const buildSelectedLifecycleItem = (
    progressItem: MemberLifecycleProgressItem | undefined,
    fallbackStageKey: string,
    fallbackSubstageKey: string,
): SelectedLifecycleItem => {
    const stage = getLifecycleStage(progressItem?.StageKey ?? fallbackStageKey);
    const substageKey = progressItem?.SubstageKey ?? fallbackSubstageKey;
    const substage = getLifecycleSubstage(stage.stageKey, substageKey);

    return {
        stage,
        substageKey,
        substage: substage.label,
        isCompleted: isProgressItemComplete(progressItem),
        isCurrent: progressItem?.IsCurrent ?? false,
        completedDate: formatLifecycleCompletedDate(progressItem?.CompletedDate),
        summary: progressItem?.Summary,
        documentation: progressItem?.Documentation,
        reasons: progressItem?.Reasons,
        notCompleteReasons: progressItem?.NotCompleteReasons,
    };
};

export const MemberLifecycleSummaryModal = ({
    selectedLifecycleItem,
    onClose,
}: {
    selectedLifecycleItem: SelectedLifecycleItem | null;
    onClose: () => void;
}) => {
    const navigate = useNavigate();
    const targetTab = selectedLifecycleItem
        ? lifecycleSubstageChartTab[`${selectedLifecycleItem.stage.stageKey}:${selectedLifecycleItem.substageKey}`]
        : undefined;

    return (
    <Modal open={Boolean(selectedLifecycleItem)} onClose={onClose}>
        <Paper
            elevation={8}
            sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '92vw', md: '78vw' },
                maxWidth: 1100,
                maxHeight: '86vh',
                overflow: 'auto',
                borderRadius: 2,
            }}
        >
            {selectedLifecycleItem && (
                <>
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${selectedLifecycleItem.stage.color}, ${selectedLifecycleItem.stage.lightColor})`,
                            color: 'common.white',
                            p: 3,
                        }}
                    >
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
                            <Box>
                                <Typography variant="h5" fontWeight={800}>
                                    {selectedLifecycleItem.stage.label}
                                </Typography>
                                <Typography variant="subtitle1">{selectedLifecycleItem.substage}</Typography>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Chip
                                    label={selectedLifecycleItem.isCurrent ? 'Current Progress' : selectedLifecycleItem.isCompleted ? 'Completed' : 'Upcoming'}
                                    sx={{ backgroundColor: 'rgba(255,255,255,0.9)', fontWeight: 700 }}
                                />
                                {selectedLifecycleItem.completedDate && (
                                    <Chip
                                        label={`Completed ${selectedLifecycleItem.completedDate}`}
                                        sx={{ backgroundColor: 'rgba(37,49,66,0.88)', color: 'common.white', fontWeight: 700 }}
                                    />
                                )}
                                <Button variant="contained" color="inherit" onClick={onClose} sx={{ color: '#253142' }}>
                                    Close
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>

                    <Box sx={{ p: 3 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {selectedLifecycleItem.stage.summary}
                        </Typography>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                            <Paper variant="outlined" sx={{ flex: 1, p: 2.5, borderTop: `5px solid ${selectedLifecycleItem.stage.color}` }}>
                                <Typography variant="h6" fontWeight={800} gutterBottom>
                                    Documentation
                                </Typography>
                                <Stack spacing={1.25}>
                                    {(
                                        selectedLifecycleItem.documentation?.length
                                            ? selectedLifecycleItem.documentation
                                            : getLifecycleSubstageFallback(selectedLifecycleItem.stage.stageKey, selectedLifecycleItem.substageKey).documentation
                                    ).map((item) => (
                                        <Box key={item} sx={{ display: 'flex', gap: 1 }}>
                                            <Box sx={{ width: 10, height: 10, mt: 0.7, borderRadius: '50%', backgroundColor: selectedLifecycleItem.stage.color }} />
                                            <Typography variant="body2">{item}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ flex: 1, p: 2.5, borderTop: `5px solid ${selectedLifecycleItem.stage.lightColor}` }}>
                                <Typography variant="h6" fontWeight={800} gutterBottom>
                                    {selectedLifecycleItem.isCompleted ? 'Stage Reasons' : 'Reasons Not Complete'}
                                </Typography>
                                <Stack spacing={1.25}>
                                    {(
                                        selectedLifecycleItem.isCompleted
                                            ? selectedLifecycleItem.reasons?.length
                                                ? selectedLifecycleItem.reasons
                                                : selectedLifecycleItem.stage.reasons
                                            : selectedLifecycleItem.notCompleteReasons?.length
                                            ? selectedLifecycleItem.notCompleteReasons
                                            : getLifecycleSubstageFallback(selectedLifecycleItem.stage.stageKey, selectedLifecycleItem.substageKey).notCompleteReasons
                                    ).map((item) => (
                                        <Box key={item} sx={{ display: 'flex', gap: 1 }}>
                                            <Box sx={{ width: 10, height: 10, mt: 0.7, borderRadius: '50%', backgroundColor: selectedLifecycleItem.stage.lightColor }} />
                                            <Typography variant="body2">{item}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>
                        </Stack>

                        <Divider sx={{ my: 3 }} />

                        <Box sx={{ backgroundColor: '#F6F8FA', borderRadius: 2, p: 2.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="subtitle1" fontWeight={800}>
                                    Summary Note
                                </Typography>
                                {selectedLifecycleItem.memberId && targetTab && (
                                    <Link
                                        component="button"
                                        underline="hover"
                                        fontWeight={700}
                                        onClick={() => {
                                            onClose();
                                            navigate(`/provider/members-list/member-chart/${selectedLifecycleItem.memberId}/${targetTab}`);
                                        }}
                                    >
                                        View Details
                                    </Link>
                                )}
                            </Stack>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                {selectedLifecycleItem.summary?.trim() || selectedLifecycleItem.stage.summary}
                            </Typography>
                        </Box>
                    </Box>
                </>
            )}
        </Paper>
    </Modal>
    );
};

const MemberLifecycleBar = ({
    memberId,
    memberLifecycle,
}: {
    memberId: string;
    memberLifecycle?: MemberLifecycle | null;
}) => {
    const dispatch = useAppDispatch();
    const { isLoading, isCalculating } = useSelector((state: RootState) => state.memberLifecycle);
    const authData = new Authorization();
    const canExecuteMemberLifecycle = authData.isInPermission('Execute Member Lifecycle');
    const [selectedLifecycleItem, setSelectedLifecycleItem] = useState<SelectedLifecycleItem | null>(null);
    const currentStageKey = memberLifecycle?.StageKey || defaultStageKey;
    const currentSubstageKey = memberLifecycle?.SubstageKey || defaultSubstageKey;
    const currentOrdinal = getStageOrdinal(currentStageKey, currentSubstageKey);
    const fillThroughOrdinal = getFillThroughOrdinal(memberLifecycle?.ProgressItems, currentOrdinal);

    const journeySummary = memberLifecycle?.JourneySummary?.trim();
    const journeySummaryParagraphs = useMemo(() => splitJourneySummaryParagraphs(journeySummary), [journeySummary]);
    const lastCalculatedLabel = memberLifecycle?.LastUpdatedDate
        ? formatUtcDateTime(memberLifecycle.LastUpdatedDate)
        : 'Not yet calculated';

    const handleRerunMemberLifecycle = () => {
        if (!memberId || isLoading || isCalculating) {
            return;
        }

        dispatch(recalculateMemberLifecycle(memberId));
    };

    return (
        <>
            <Paper square elevation={3} sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', p: 1.5 }}>
                <Box
                    sx={{
                        backgroundColor: '#F6F8FA',
                        borderRadius: '4px 4px 0 0',
                        px: 2,
                        py: 1.25,
                        mb: 0.5,
                        borderBottom: '1px solid rgba(37, 49, 66, 0.12)',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: journeySummary ? 0.5 : 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" fontWeight={700} sx={{ color: '#253142' }}>
                                Member Journey
                            </Typography>
                            {canExecuteMemberLifecycle && (
                                <Tooltip title="Re-run member lifecycle">
                                    <span>
                                        <IconButton
                                            size="small"
                                            aria-label="Re-run member lifecycle"
                                            onClick={handleRerunMemberLifecycle}
                                            disabled={isLoading || isCalculating || !memberId}
                                            sx={{
                                                color: '#253142',
                                                p: 0.25,
                                                ...(isCalculating && {
                                                    animation: 'spin 1.2s linear infinite',
                                                    '@keyframes spin': {
                                                        '0%': { transform: 'rotate(0deg)' },
                                                        '100%': { transform: 'rotate(360deg)' },
                                                    },
                                                }),
                                            }}
                                        >
                                            <AutorenewIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {isCalculating ? 'Calculating…' : `Last calculated: ${lastCalculatedLabel}`}
                        </Typography>
                    </Box>
                    {journeySummaryParagraphs.length > 0 && (
                        <Box>
                            {journeySummaryParagraphs.map((paragraph, index) => (
                                <Typography
                                    key={`journey-summary-${index}`}
                                    variant="body2"
                                    sx={{
                                        color: '#253142',
                                        lineHeight: 1.55,
                                        fontSize: '0.8125rem',
                                        mb: index < journeySummaryParagraphs.length - 1 ? 1 : 0,
                                    }}
                                >
                                    {paragraph}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Box>
                <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }} tabIndex={0} role="region" aria-label="Member lifecycle chart">
                    <Box sx={{ width: '100%', minWidth: (theme) => `calc(${minimumChartWidth}px + ${theme.spacing(4)})`, p: 2, boxSizing: 'border-box' }}>
                        <Box sx={{ display: 'flex', minHeight: 28 }}>
                            <Box
                                sx={{
                                    width: leftColumnWidth,
                                    flexShrink: 0,
                                    backgroundColor: '#253142',
                                    color: 'common.white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1.5,
                                }}
                            >
                                <Typography variant="caption" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                                    Member Lifecycle
                                </Typography>
                            </Box>
                            {lifecycleStages.map((stage, index) => {
                                const isLastStage = index === lifecycleStages.length - 1;
                                return (
                                    <Box
                                        key={stage.label}
                                        sx={{
                                            flex: `${stage.substages.length} 1 0`,
                                            minWidth: 0,
                                            backgroundColor: stage.color,
                                            color: 'common.white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 700,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            py: 0.25,
                                            pl: 0.5,
                                            // Keep the label clear of the arrow notch carved out on the right.
                                            pr: isLastStage ? 0.5 : '22px',
                                            clipPath: isLastStage
                                                ? undefined
                                                : 'polygon(0 0, calc(100% - 26px) 0, 100% 50%, calc(100% - 26px) 100%, 0 100%)',
                                            ml: index === 0 ? 0 : '-1px',
                                            zIndex: lifecycleStages.length - index,
                                        }}
                                    >
                                        <Tooltip title={stage.label}>
                                            <Typography
                                                variant="caption"
                                                fontWeight={700}
                                                sx={{
                                                    textAlign: 'center',
                                                    lineHeight: 1.05,
                                                    fontSize: { xs: '0.6rem', sm: '0.68rem', md: '0.75rem' },
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {stage.label}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box sx={{ display: 'flex', minHeight: 34 }}>
                            <Box
                                sx={{
                                    width: leftColumnWidth,
                                    flexShrink: 0,
                                    backgroundColor: '#2F3A4A',
                                    color: 'common.white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1.5,
                                    borderTop: '1px solid rgba(255,255,255,0.18)',
                                }}
                            >
                                <Typography variant="caption">Sub Phase</Typography>
                            </Box>
                            {lifecycleStages.flatMap((stage) =>
                                stage.substages.map((substage) => (
                                    <Box
                                        key={`${stage.stageKey}-${substage.substageKey}`}
                                        sx={{
                                            flex: '1 1 0',
                                            minWidth: 0,
                                            backgroundColor: '#344254',
                                            color: 'common.white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textAlign: 'center',
                                            px: 0.5,
                                            py: 0.25,
                                            overflow: 'hidden',
                                            borderLeft: '1px solid rgba(255,255,255,0.16)',
                                        }}
                                    >
                                        <Tooltip title={substage.label}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    lineHeight: 1.1,
                                                    fontSize: { xs: '0.58rem', sm: '0.64rem', md: '0.7rem' },
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {substage.label}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                )),
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', height: 92 }}>
                            <Box
                                sx={{
                                    width: leftColumnWidth,
                                    flexShrink: 0,
                                    backgroundColor: '#253142',
                                    color: 'common.white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1.5,
                                    borderTop: '1px solid rgba(255,255,255,0.18)',
                                }}
                            >
                                <Typography variant="caption" sx={{ lineHeight: 1.15 }}>
                                    Key Milestone and Projects
                                </Typography>
                            </Box>
                            {lifecycleStages.flatMap((stage) =>
                                stage.substages.map((substage) => {
                                    const progressItem = getProgressItem(memberLifecycle, stage.stageKey, substage.substageKey);
                                    const substageOrdinal = getStageOrdinal(stage.stageKey, substage.substageKey);
                                    const isCompleted = substageOrdinal <= fillThroughOrdinal;
                                    const isCurrent = isSubstageCurrentProgress(substageOrdinal, fillThroughOrdinal);
                                    const completedDate = formatLifecycleCompletedDate(progressItem?.CompletedDate);

                                    return (
                                        <Box
                                            key={`${stage.stageKey}-${substage.substageKey}-progress`}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() =>
                                                setSelectedLifecycleItem({
                                                    ...buildSelectedLifecycleItem(progressItem, stage.stageKey, substage.substageKey),
                                                    memberId,
                                                    isCompleted,
                                                    isCurrent,
                                                    completedDate,
                                                })
                                            }
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    setSelectedLifecycleItem({
                                                        ...buildSelectedLifecycleItem(progressItem, stage.stageKey, substage.substageKey),
                                                        memberId,
                                                        isCompleted,
                                                        isCurrent,
                                                        completedDate,
                                                    });
                                                }
                                            }}
                                            sx={{
                                                flex: '1 1 0',
                                                minWidth: 0,
                                                backgroundColor: isCompleted ? stage.color : stage.lightColor,
                                                opacity: isCompleted ? 1 : 0.42,
                                                borderLeft: '1px solid rgba(255,255,255,0.18)',
                                                position: 'relative',
                                                cursor: 'pointer',
                                                transition: 'opacity 0.2s ease, filter 0.2s ease',
                                                '&:hover': {
                                                    opacity: 1,
                                                    filter: 'brightness(1.08)',
                                                },
                                                '&:focus-visible': {
                                                    outline: '3px solid #253142',
                                                    outlineOffset: -3,
                                                },
                                            }}
                                        >
                                            {completedDate && (
                                                <Stack
                                                    spacing={0.5}
                                                    sx={{
                                                        height: '100%',
                                                        width: '100%',
                                                        minWidth: 0,
                                                        boxSizing: 'border-box',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'common.white',
                                                        textAlign: 'center',
                                                        px: 0.5,
                                                        textShadow: '0 1px 2px rgba(0,0,0,0.35)',
                                                    }}
                                                >
                                                    <Typography variant="caption" fontWeight={800} sx={{ lineHeight: 1, maxWidth: '100%', overflowWrap: 'anywhere' }}>
                                                        Completed
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                                            color: '#253142',
                                                            borderRadius: 10,
                                                            maxWidth: '100%',
                                                            minWidth: 0,
                                                            boxSizing: 'border-box',
                                                            px: 0.85,
                                                            py: 0.25,
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                                                        }}
                                                    >
                                                        <Typography variant="caption" fontWeight={800} sx={{ lineHeight: 1, overflowWrap: 'anywhere' }}>
                                                            {completedDate}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            )}
                                            {isCurrent && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        right: -5,
                                                        top: -5,
                                                        bottom: -5,
                                                        width: 12,
                                                        backgroundColor: 'common.white',
                                                        border: '2px solid #253142',
                                                        borderRadius: 8,
                                                        boxShadow:
                                                            '0 0 0 3px rgba(255,255,255,0.85), 0 0 18px 6px rgba(255,255,255,0.95), 0 0 20px 8px rgba(37,49,66,0.55)',
                                                        zIndex: 2,
                                                        '&::before': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            left: '50%',
                                                            top: 5,
                                                            bottom: 5,
                                                            width: 3,
                                                            transform: 'translateX(-50%)',
                                                            borderRadius: 2,
                                                            backgroundColor: '#253142',
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    );
                                }),
                            )}
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <MemberLifecycleSummaryModal selectedLifecycleItem={selectedLifecycleItem} onClose={() => setSelectedLifecycleItem(null)} />
        </>
    );
};

export default MemberLifecycleBar;
